import React, { FC, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Item, ItemTagSummary } from '../../../app/services/api/orderManagementClient';
import { PromotionForCustomerDto } from '../../../app/services/api/promotions';
import { applyRoundAndFormat, formatPromotionReservationDuration } from '../../../utils';
import { ItemTagChip } from '../../orderly-for-distributors/ItemTags/components/ItemTagChip';

const ItemTagRow: FC<{ tags: ItemTagSummary[] }> = ({ tags }) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const visible = tags.slice(0, 2);
  const overflow = tags.slice(2);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0, mb: 0 }}>
      {visible.map((tag) => (
        <ItemTagChip
          key={tag.id}
          name={tag.name}
          textColor={tag.textColor}
          backgroundColor={tag.backgroundColor}
          iconKey={tag.iconKey}
          isActive={true}
          placeholderLabel=""
        />
      ))}
      {overflow.length > 0 && (
        <Tooltip
          open={overflowOpen}
          onClose={() => setOverflowOpen(false)}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="top"
          arrow
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, p: 0.5 }}>
              {overflow.map((tag) => (
                <ItemTagChip
                  key={tag.id}
                  name={tag.name}
                  textColor={tag.textColor}
                  backgroundColor={tag.backgroundColor}
                  iconKey={tag.iconKey}
                  isActive={true}
                  placeholderLabel=""                />
              ))}
            </Box>
          }
        >
          <Box
            onClick={(e) => { e.stopPropagation(); setOverflowOpen((v) => !v); }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 22,
              px: 1,
              borderRadius: '999px',
              backgroundColor: 'action.hover',
              fontSize: '10px',
              fontWeight: 700,
              cursor: 'pointer',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            +{overflow.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

type Props = {
  product: Item;
  currencyCode?: string;
  basketQuantity?: number;
  promoQuantity?: number;
  promotion?: PromotionForCustomerDto;
  onAddToOrder: (product: Item) => void;
  onDecreaseFromOrder: (product: Item) => void;
  onSetQuantity: (product: Item, quantity: number) => void;
  onSetPromoQuantity?: (product: Item, quantity: number) => Promise<{ success: boolean; message?: string }>;
};

const ProductCard: FC<Props> = ({ product, currencyCode, basketQuantity = 0, promoQuantity = 0, promotion, onAddToOrder, onDecreaseFromOrder, onSetQuantity, onSetPromoQuantity }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('storefront');
  const [imgError, setImgError] = useState(false);
  const [editingQty, setEditingQty] = useState(false);
  const [inputQty, setInputQty] = useState('');

  useEffect(() => {
    if (editingQty) {
      setInputQty(String(basketQuantity));
    }
  }, [basketQuantity, editingQty]);

  const hasImage = !!product.thumbnailUrl && !imgError;

  const [editingPromoQty, setEditingPromoQty] = useState(false);
  const [inputPromoQty, setInputPromoQty] = useState('');

  useEffect(() => {
    if (editingPromoQty) {
      setInputPromoQty(String(promoQuantity));
    }
  }, [promoQuantity, editingPromoQty]);

  const hasPromotion = Boolean(promotion);
  const isPromoSoldOut = Boolean(promotion && (promotion.remainingQuantity ?? 0) <= 0);
  const promoUnitPrice = promotion
    ? promotion.pricingType === 2
      ? promotion.fixedPromoUnitPrice
      : product.price * (1 - (promotion.discountPercentage ?? 0) / 100)
    : null;

  const getPromoEndDate = () => {
    if (!promotion?.endDateUtc) {
      return null;
    }
    const end = new Date(promotion.endDateUtc);
    if (Number.isNaN(end.getTime()) || end.getFullYear() <= 1) {
      return null;
    }
    return end.toLocaleDateString();
  };

  const renderPromoTooltipContent = () => {
    if (!promotion || promoUnitPrice == null) return null;
    const reservationDuration = formatPromotionReservationDuration(promotion.reservationDurationMinutes);
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>{promotion.name}</Typography>
        {promotion.promoDescription && (
          <Typography sx={{ fontSize: '11px', mb: 0.5 }}>{promotion.promoDescription}</Typography>
        )}
        <Typography sx={{ fontSize: '11px' }}>
          {promotion.discountPercentage != null
            ? `${promotion.discountPercentage}% ${t('promoDiscount')}`
            : `${t('promoFixedPrice')}: ${applyRoundAndFormat(promoUnitPrice, currencyCode)}`}
        </Typography>
        <Typography sx={{ fontSize: '11px', mt: 0.25 }}>
          {t('remaining')}: {promotion.remainingQuantity} / {promotion.quantityCap}
        </Typography>
        {reservationDuration && (
          <Typography sx={{ fontSize: '11px', mt: 0.25 }}>{reservationDuration}</Typography>
        )}
      </Box>
    );
  };

  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode || 'GBP',
        minimumFractionDigits: 2,
      }).format(value);
    } catch {
      return `£${value.toFixed(2)}`;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF', 
        borderRadius: '5px',
        border: 'none',
        boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        padding: '8px',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.10)',
        },
      }}
    >
      {/* Image section */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        {hasImage ? (
          <Box
            component="img"
            src={product.thumbnailUrl}
            alt={product.name}
            onError={() => setImgError(true)}
            sx={{
              width: '70%',
              height: '70%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '8px',
              backgroundColor: theme.palette.custom.gray[200],
            }}
          />
        )}
        {(product.itemTags ?? []).length > 0 && (
          <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1, maxWidth: 'calc(100% - 8px)' }}>
            <ItemTagRow tags={product.itemTags!} />
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        {product.category && (
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: theme.palette.custom.gray[400], textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {product.category}
          </Typography>
        )}

        <Typography
          onClick={() => navigate(`/ofd/products/${product.sku}`)}
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
            minHeight: '2.4em',
            cursor: 'pointer',
            transition: 'opacity 0.15s ease',
            '&:hover': {
              opacity: 0.6,
            },
          }}
        >
          {product.name}
        </Typography>

        <Typography sx={{ fontSize: '11px', color: theme.palette.custom.gray[500] }}>
          {t('itemNumber')}: {product.sku}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: '6px' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, alignSelf: 'flex-end' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, mr: '6px' }}>
              {t('pricePerUom', {
                price: formatPrice(product.price),
                uom: product.caseSize != null
                  ? `${product.orderableUom} (${product.caseSize})`
                  : product.orderableUom,
              })}
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            {editingQty ? (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#1B4332',
                  borderRadius: '999px',
                  height: 28,
                  overflow: 'hidden',
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onDecreaseFromOrder(product); }}
                  title={t('removeFromDelivery')}
                  disabled={basketQuantity === 0}
                  sx={{
                    color: '#fff',
                    width: 28,
                    height: 28,
                    borderRadius: 0,
                    '&:hover': { backgroundColor: '#143728' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Box
                  component="input"
                  type="number"
                  value={inputQty}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onChange={(e: any) => setInputQty(e.target.value)}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onBlur={(e: any) => {
                    setEditingQty(false);
                    const parsed = parseInt(e.target.value, 10);
                    if (!isNaN(parsed) && parsed >= 0 && parsed !== basketQuantity) {
                      onSetQuantity(product, parsed);
                    }
                    setInputQty('');
                  }}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter') e.target.blur();
                    else if (e.key === 'Escape') { setEditingQty(false); setInputQty(''); }
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  placeholder="0"
                  sx={{
                    width: 32,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 700,
                    textAlign: 'center',
                    px: '4px',
                    MozAppearance: 'textfield',
                    '&::placeholder': { color: 'rgba(255,255,255,0.6)', fontWeight: 400 },
                    '&::-webkit-outer-spin-button': { display: 'none' },
                    '&::-webkit-inner-spin-button': { display: 'none' },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onAddToOrder(product); }}
                  title={t('addToDelivery')}
                  sx={{
                    color: '#fff',
                    width: 28,
                    height: 28,
                    borderRadius: 0,
                    '&:hover': { backgroundColor: '#143728' },
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#1B4332',
                  borderRadius: '999px',
                  height: 28,
                  overflow: 'hidden',
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onDecreaseFromOrder(product); }}
                  title={t('removeFromDelivery')}
                  disabled={basketQuantity === 0}
                  sx={{
                    color: '#fff',
                    width: 28,
                    height: 28,
                    borderRadius: 0,
                    '&:hover': { backgroundColor: '#143728' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography
                  title={t('editQuantity')}
                  onClick={(e) => { e.stopPropagation(); setEditingQty(true); setInputQty(String(basketQuantity)); }}
                  sx={{ color: '#fff', fontSize: '12px', fontWeight: 700, minWidth: 20, textAlign: 'center', px: '4px', userSelect: 'none', cursor: 'text' }}
                >
                  {basketQuantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onAddToOrder(product); }}
                  title={t('addToDelivery')}
                  sx={{
                    color: '#fff',
                    width: 28,
                    height: 28,
                    borderRadius: 0,
                    '&:hover': { backgroundColor: '#143728' },
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        {/* Promo row */}
        {hasPromotion && promoUnitPrice != null && (
          <Box sx={{ mt: '6px', pt: '6px', borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: '4px' }}>
              <LocalOfferIcon sx={{ fontSize: 12, color: '#2E7D32', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('promoAllocation')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', color: theme.palette.custom.gray[400], textDecoration: 'line-through' }}>
                      {formatPrice(product.price)}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2E7D32' }}>
                      {applyRoundAndFormat(promoUnitPrice, currencyCode)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography sx={{ fontSize: '10px', color: isPromoSoldOut ? theme.palette.error.main : '#2E7D32', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {isPromoSoldOut ? t('promoSoldOut') : <>{promotion!.remainingQuantity} {t('remaining')}</>}
                    </Typography>
                    <Tooltip title={renderPromoTooltipContent()}
                      arrow
                      placement="top">
                      <InfoOutlinedIcon sx={{ fontSize: 12, color: theme.palette.custom.gray[400], cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                  {getPromoEndDate() && (
                    <Typography sx={{ fontSize: '10px', color: theme.palette.custom.gray[500], mt: 0.25 }}>
                      {t('promoEndDate', { date: getPromoEndDate() })}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {editingPromoQty ? (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#2E7D32',
                      borderRadius: '999px',
                      height: 28,
                      overflow: 'hidden',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); const next = Math.max(0, promoQuantity - 1); onSetPromoQuantity?.(product, next); }}
                      disabled={promoQuantity === 0}
                      sx={{
                        color: '#fff',
                        width: 28,
                        height: 28,
                        borderRadius: 0,
                        '&:hover': { backgroundColor: '#1B5E20' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Box
                      component="input"
                      type="number"
                      value={inputPromoQty}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onChange={(e: any) => setInputPromoQty(e.target.value)}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onBlur={(e: any) => {
                        setEditingPromoQty(false);
                        const parsed = parseInt(e.target.value, 10);
                        if (!isNaN(parsed) && parsed >= 0 && parsed !== promoQuantity) {
                          const capped = Math.min(parsed, promotion!.remainingQuantity);
                          onSetPromoQuantity?.(product, capped);
                        }
                        setInputPromoQty('');
                      }}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onKeyDown={(e: any) => {
                        if (e.key === 'Enter') e.target.blur();
                        else if (e.key === 'Escape') { setEditingPromoQty(false); setInputPromoQty(''); }
                      }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      placeholder="0"
                      sx={{
                        width: 32,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 700,
                        textAlign: 'center',
                        px: '4px',
                        MozAppearance: 'textfield',
                        '&::placeholder': { color: 'rgba(255,255,255,0.6)', fontWeight: 400 },
                        '&::-webkit-outer-spin-button': { display: 'none' },
                        '&::-webkit-inner-spin-button': { display: 'none' },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); const next = Math.min(promoQuantity + 1, promotion!.remainingQuantity); onSetPromoQuantity?.(product, next); }}
                      disabled={promoQuantity >= promotion!.remainingQuantity}
                      title={t('addPromo')}
                      sx={{
                        color: '#fff',
                        width: 28,
                        height: 28,
                        borderRadius: 0,
                        '&:hover': { backgroundColor: '#1B5E20' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#2E7D32',
                      borderRadius: '999px',
                      height: 28,
                      overflow: 'hidden',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); const next = Math.max(0, promoQuantity - 1); onSetPromoQuantity?.(product, next); }}
                      disabled={promoQuantity === 0}
                      sx={{
                        color: '#fff',
                        width: 28,
                        height: 28,
                        borderRadius: 0,
                        '&:hover': { backgroundColor: '#1B5E20' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography
                      title={t('editQuantity')}
                      onClick={(e) => { e.stopPropagation(); setEditingPromoQty(true); setInputPromoQty(String(promoQuantity)); }}
                      sx={{ color: '#fff', fontSize: '12px', fontWeight: 700, minWidth: 20, textAlign: 'center', px: '4px', userSelect: 'none', cursor: 'text' }}
                    >
                      {promoQuantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); const next = Math.min(promoQuantity + 1, promotion!.remainingQuantity); onSetPromoQuantity?.(product, next); }}
                      disabled={promoQuantity >= promotion!.remainingQuantity}
                      title={t('addPromo')}
                      sx={{
                        color: '#fff',
                        width: 28,
                        height: 28,
                        borderRadius: 0,
                        '&:hover': { backgroundColor: '#1B5E20' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProductCard;